import React, {
  useCallback, useMemo, useState, useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  TreeItem, TreeItemActions, TreeItemAction, TreeMenu, TreeMenuItem, Icon,
} from '@picsio/ui';
import {
  Inbox, Web, TrashIcon, ThreePointsIcon, Edit, Copy, CheckIcon,
} from '@picsio/icons';
import {
  arrayOf, bool, func, shape, string, number,
} from 'prop-types';
import l10n from '../../shared/strings';
import { validateCollectionTreeItemName } from '../../helpers/collectionName';
import { remove, rename } from '../../store/inboxes/actions';
import { decodeSlash } from '../../shared/utils';
import Logger from '../../services/Logger';
import { navigate } from '../../helpers/history';
import { showDialog } from '../dialog';
import copyTextToClipboard from '../../helpers/copyTextToClipboard';
import makeAlias from '../../helpers/makeAlias';
import Tooltip from '../Tooltip';

const normalizeName = (name) => decodeSlash(name.trim().toLowerCase());

const Item = ({
  node,
  isSelected,
  onSelect,
  alreadyUsedNames,
  fullInboxAccess,
  assetsUploadedPerMonth,
  onAction,
}) => {
  const dispatch = useDispatch();

  const [renameInProgress, setRenameInProgress] = useState(false);
  const [isBusy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCopied, setCopied] = useState(false);

  const menuRef = useRef();

  const handleToggleMenu = useCallback((event) => {
    if (event) event.stopPropagation();
    setMenuOpen((open) => !open);
  }, [setMenuOpen]);

  const handleClickSettings = useCallback((event) => {
    event.stopPropagation();

    /** Item component should not know about the navigation and just notify parent about the action, and parent will decide what to do with it. */
    onAction?.('settings');

    Logger.log('User', 'InboxSettings', { collectionId: node._id });
    navigate(`/inbox/${node._id}?tab=main`);
  }, [node._id, onAction]);

  const handleClickDelete = useCallback((event) => {
    event.stopPropagation();

    showDialog({
      title: l10n.INBOXSETTINGS.DELETE.title,
      text: l10n.INBOXSETTINGS.DELETE.getText(node.name),
      textBtnCancel: l10n.INBOXSETTINGS.DELETE.textBtnCancel,
      textBtnOk: l10n.INBOXSETTINGS.DELETE.textBtnOk,
      onOk: () => {
        Logger.log('User', 'InboxesPanelDeleteInboxModalOkClicked');
        dispatch(remove(node._id, node.name));
      },
      onClose: () => {
        Logger.log('User', 'InboxesPanelDeleteInboxModalClosed');
      },
      onCancel: () => {
        Logger.log('User', 'InboxesPanelDeleteInboxModalCanceled');
      },
    });
    onAction?.('delete', { inboxId: node._id });
  }, [node.name, node._id, onAction, dispatch]);

  const handleClickRename = useCallback((e) => {
    e.stopPropagation();
    setRenameInProgress(true);
    onAction?.('rename', { inboxId: node._id });
  }, [node._id, onAction]);

  const handleEditName = useCallback(async (value) => {
    const newName = value.trim();
    Logger.log('User', 'InboxesPanelRenameInboxOK');

    if (newName !== node.name) {
      setBusy(true);
      await dispatch(rename({ id: node._id, name: newName }));
      setBusy(false);
    }
    setRenameInProgress(false);
  }, [dispatch, node]);

  const handleEditCancel = useCallback(() => {
    setRenameInProgress(false);
  }, []);

  /**
   * Validate name
   * @param {string} value
   * @returns {boolean}
   */
  const validateName = useCallback(
    (value) => validateCollectionTreeItemName(value, alreadyUsedNames, normalizeName),
    [alreadyUsedNames],
  );

  const handleClickCopyLink = useCallback((event) => {
    if (!node?.isShared) return;

    event.stopPropagation();
    setCopied(true);

    const inboxURL = makeAlias(node.alias);
    copyTextToClipboard(inboxURL, l10n.DETAILS.inboxUrlCopied);
    onAction?.('copyLink', { inboxId: node._id });
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [node._id, node.alias, node?.isShared, onAction]);

  const actions = useMemo(() => {
    const listActions = [
      {
        text: l10n.INBOXESTREE.textCopyInboxLink,
        tooltip: !node?.isShared ? 'Publish inbox to copy link' : '',
        onClick: handleClickCopyLink,
        icon: (
          <Icon>
            <Choose>
              <When condition={isCopied}>
                <CheckIcon className="checkIcon" />
              </When>
              <Otherwise>
                <Copy />
              </Otherwise>
            </Choose>
          </Icon>
        ),
        disabled: !node?.isShared,
        testId: 'inboxCopyLink',
      },
      {
        text: l10n.INBOXESTREE.textInboxSettings,
        onClick: handleClickSettings,
        icon: <Web />,
        testId: 'inboxSetting',
      },
    ];

    if (fullInboxAccess) {
      listActions.push({
        text: l10n.INBOXESTREE.textDelete,
        onClick: handleClickDelete,
        icon: <TrashIcon />,
        testId: 'inboxDelete',
      });
    }

    listActions.push({
      text: l10n.INBOXESTREE.textRename,
      onClick: handleClickRename,
      icon: <Edit />,
      testId: 'inboxRename',
    });

    const buttons = window.innerWidth < 1024 ? [] : listActions.slice(0, 2);
    const menuItems = window.innerWidth < 1024 ? listActions : listActions.slice(2);

    return (
      <TreeItemActions>
        <If condition={!fullInboxAccess}>
          <div className="inboxesTreeItem__assetsUsed">
            {`${assetsUploadedPerMonth}/100 assets used`}
          </div>
        </If>
        {buttons.map(({
          text, icon, onClick, testId, tooltip = '', disabled = false,
        }) => (
          <Choose>
            <When condition={disabled && tooltip}>
              <Tooltip content={tooltip} placement="top">
                <TreeItemAction
                  key={text}
                  tooltip={tooltip || text}
                  disabled={disabled}
                  onClick={onClick}
                  componentProps={{ 'data-testid': testId }}
                >
                  {icon}
                </TreeItemAction>
              </Tooltip>
            </When>
            <Otherwise>
              <TreeItemAction
                key={text}
                tooltip={tooltip || text}
                disabled={disabled}
                onClick={onClick}
                componentProps={{ 'data-testid': testId }}
              >
                {icon}
              </TreeItemAction>
            </Otherwise>
          </Choose>

        ))}
        <If condition={menuItems.length}>
          <TreeItemAction
            ref={menuRef}
            onClick={handleToggleMenu}
            componentProps={{ 'data-testid': 'menuOpener' }}
          >
            <ThreePointsIcon />
          </TreeItemAction>
          <TreeMenu ref={menuRef} isOpen={menuOpen} onClose={handleToggleMenu}>
            {menuItems.map(({
              text, onClick, icon, testId,
            }) => (
              <TreeMenuItem
                key={text}
                text={text}
                onClick={(...args) => {
                  const [event] = args;
                  event.stopPropagation();
                  setMenuOpen(false);
                  onClick(...args);
                }}
                icon={icon}
                data-testid={testId}
              />
            ))}
          </TreeMenu>
        </If>
      </TreeItemActions>
    );
  }, [
    assetsUploadedPerMonth,
    fullInboxAccess,
    handleClickCopyLink,
    handleClickDelete,
    handleClickRename,
    handleClickSettings,
    handleToggleMenu,
    menuOpen,
    isCopied,
    node?.isShared,
  ]);

  const icon = useMemo(() => {
    if (node.isShared) return <Web />;
    return <Inbox />;
  }, [node]);

  return (
    <TreeItem
      nodeId={node._id}
      name={decodeSlash(node.name)}
      level={1}
      icon={icon}
      selected={isSelected}
      onClick={onSelect}
      actions={actions}
      persistActions={menuOpen}
      edit={renameInProgress}
      editValidation={validateName}
      onEdit={handleEditName}
      onEditCancel={handleEditCancel}
      disabled={isBusy || node.isBusy}
      editable
      canDrop={false}
    />
  );
};

Item.defaultProps = {
  alreadyUsedNames: [],
  isSelected: false,
  fullInboxAccess: false,
  assetsUploadedPerMonth: 0,
  onAction: undefined,
};

Item.propTypes = {
  node: shape({
    name: string,
    _id: string,
    isBusy: bool,
    alias: string,
    isShared: bool,
  }).isRequired,
  onSelect: func.isRequired,
  alreadyUsedNames: arrayOf(string),
  isSelected: bool,
  fullInboxAccess: bool,
  assetsUploadedPerMonth: number,
  onAction: func,
};

export default Item;
